import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageLoadError } from "./NoAccess";
import { Loader2, Save, Plus, Trash2, RotateCcw, Ban, Table2 } from "lucide-react";
import { parsePrice, publicCurrencies, hasPositiveMonthly } from "./moneyDisplay";
import { OPERATING_CURRENCIES, currencyLabel } from "../lib/currencies";
// Relative, not "@/...": validation infrastructure, resolved for REAL under Jest (no "@"
// alias there) — see lib/validation.js / hooks/useFieldErrors.js headers. useFieldErrors
// itself is not used here: this screen validates many independent plan cards plus a
// separate create dialog, never one shared flat form the hook's single-map shape fits.
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import MediaPickerField from "./site/MediaPickerField";

const PLAN_ID_RE = /^[a-z][a-z0-9_]{1,31}$/;

// Shape: `{usd: 49, cad: 65}` (a stored grid, lowercase keys, major units) →
// `{usd: "49", cad: "65"}` (one raw input string per operating currency, "" = unpriced).
const toPriceStrings = (prices) => {
  const out = {};
  OPERATING_CURRENCIES.forEach((c) => { out[c] = prices?.[c] != null ? String(prices[c]) : ""; });
  return out;
};

// The inverse, for the PUT/POST body: blanks are OMITTED, never sent as 0 (see
// `parsePrice`). Only called on a draft `validatePrices` has already accepted.
const toPriceGrid = (strings) => {
  const out = {};
  OPERATING_CURRENCIES.forEach((c) => {
    const v = parsePrice(strings[c]).value;
    if (v !== undefined) out[c] = v;
  });
  return out;
};

const draftFromPlan = (p) => ({
  name: p.name || "",
  description: p.description || "",
  tagline: p.tagline || "",
  order: String(p.order ?? 0),
  monthly: toPriceStrings(p.prices),
  annual: toPriceStrings(p.annual_prices),
  // The plan's image in the platform media library (contract §3.5): the id is what is
  // stored, the url only what the card previews.
  image: p.image_media_id ? { media_id: p.image_media_id, url: p.image_url || "" } : null,
});

const emptyCreateForm = () => ({
  plan_id: "", name: "", description: "", tagline: "", order: "100", active: true,
  monthly: toPriceStrings({}),
  annual: toPriceStrings({}),
});

// Erreurs par devise pour un jeu {monthly, annual} de chaînes brutes — objet vide = valide.
function validatePrices(t, d) {
  const errors = { monthly: {}, annual: {} };
  OPERATING_CURRENCIES.forEach((c) => {
    const m = parsePrice(d.monthly[c]);
    if (!m.ok) errors.monthly[c] = t("plans.priceInvalid");
    const a = parsePrice(d.annual[c]);
    if (!a.ok) errors.annual[c] = t("plans.priceInvalid");
  });
  return errors;
}
const priceErrorCount = (errors) => Object.keys(errors.monthly).length + Object.keys(errors.annual).length;

const isValidOrder = (raw) => {
  const s = String(raw ?? "").trim();
  return /^\d+$/.test(s);
};

/**
 * Click-time validation for one plan card. Runs the same predicates the Save button used
 * to stay disabled on, now only when Save is actually clicked — house rule: never disable
 * Save for content validity, judge it on click so the message can point at the offending
 * field instead of leaving the button mutely inert. Returns `null` when the draft is
 * valid, or a per-field error map otherwise.
 */
function computePlanErrors(t, d) {
  const priceErrors = validatePrices(t, d);
  const orderOk = isValidOrder(d.order);
  const positiveOk = hasPositiveMonthly(d);
  if (priceErrorCount(priceErrors) === 0 && orderOk && positiveOk) return null;
  return {
    monthly: priceErrors.monthly,
    annual: priceErrors.annual,
    order: orderOk ? null : t("plans.orderInvalid"),
    priceOneRequired: positiveOk ? null : t("plans.priceOneRequired"),
  };
}

/** Same idea for the create dialog, plus the two fields only creation has (plan_id, name).
 *  `tc` is the common-namespace `t`: "required field" for a blank name is a generic
 *  message, not a super-admin-specific one, so it is not a `plans.*` key. */
function computeCreateErrors(t, tc, d) {
  const priceErrors = validatePrices(t, d);
  const orderOk = isValidOrder(d.order);
  const positiveOk = hasPositiveMonthly(d);
  const planIdOk = PLAN_ID_RE.test(d.plan_id || "");
  const nameOk = !!d.name?.trim();
  if (priceErrorCount(priceErrors) === 0 && orderOk && positiveOk && planIdOk && nameOk) return null;
  return {
    monthly: priceErrors.monthly,
    annual: priceErrors.annual,
    order: orderOk ? null : t("plans.orderInvalid"),
    priceOneRequired: positiveOk ? null : t("plans.priceOneRequired"),
    planId: planIdOk ? null : t("plans.planIdInvalid"),
    name: nameOk ? null : tc("validation.required"),
  };
}

/** Says where the plan actually appears publicly, live as the prices are typed. */
function PublicVisibility({ t, draft, planId }) {
  const { shown, hidden } = publicCurrencies(draft);
  if (shown.length === 0) return null;   // the "at least one price" error already speaks
  return (
    <p className="text-[11px] text-slate-500 mt-1" data-testid={`plan-public-in-${planId}`}>
      {hidden.length === 0
        ? t("plans.publicInAll")
        : t("plans.publicIn", {
          shown: shown.map(currencyLabel).join(", "),
          hidden: hidden.map(currencyLabel).join(", "),
        })}
    </p>
  );
}

export default function PlansManager() {
  const t = useT("superadmin");
  const tc = useT("common");
  const [plans, setPlans] = useState([]);
  const [drafts, setDrafts] = useState({});
  // Field errors per plan_id, populated only by a failed `save()` click (never live as the
  // draft is typed) and cleared per-field on the next relevant keystroke — same contract as
  // useFieldErrors, reimplemented here because this screen holds many independent cards.
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm());
  const [createErrors, setCreateErrors] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    let data;
    try {
      ({ data } = await api.get("/super-admin/plans"));
    } catch (e) {
      setLoadError(e);
      setLoading(false);
      return;
    }
    setPlans(data);
    const d = {};
    data.forEach((p) => { d[p.plan_id] = draftFromPlan(p); });
    setDrafts(d);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const clearPlanError = (planId, kind, cur) => {
    setErrors((prev) => {
      const e = prev[planId];
      if (!e) return prev;
      const next = { ...e };
      if (kind === "order") {
        next.order = null;
      } else {
        next[kind] = { ...next[kind] };
        delete next[kind][cur];
        if (kind === "monthly") next.priceOneRequired = null;
      }
      return { ...prev, [planId]: next };
    });
  };

  const updateField = (planId, field, value) => {
    setDrafts((prev) => ({ ...prev, [planId]: { ...prev[planId], [field]: value } }));
    if (field === "order") clearPlanError(planId, "order");
  };
  const updatePrice = (planId, kind, cur, value) => {
    setDrafts((prev) => ({
      ...prev,
      [planId]: { ...prev[planId], [kind]: { ...prev[planId][kind], [cur]: value } },
    }));
    clearPlanError(planId, kind, cur);
  };

  const save = async (plan) => {
    const d = drafts[plan.plan_id];
    const err = computePlanErrors(t, d);
    if (err) {
      setErrors((prev) => ({ ...prev, [plan.plan_id]: err }));
      toast.error(tc("validation.fixHighlighted"));
      return;
    }
    setErrors((prev) => ({ ...prev, [plan.plan_id]: null }));
    setSaving((p) => ({ ...p, [plan.plan_id]: true }));
    try {
      await api.put(`/super-admin/plans/${plan.plan_id}`, {
        name: d.name,
        description: d.description,
        tagline: d.tagline,
        order: Number(d.order),
        prices: toPriceGrid(d.monthly),
        annual_prices: toPriceGrid(d.annual),
        image_media_id: d.image ? d.image.media_id : null,
      });
      toast.success(t("plans.updated"));
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("plans.error"), t));
    } finally {
      setSaving((p) => ({ ...p, [plan.plan_id]: false }));
    }
  };

  const toggleActive = async (p) => {
    const activating = !p.active;
    const msg = activating
      ? t("plans.confirmReactivate", { name: p.name })
      : t("plans.confirmRetire", { name: p.name });
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/super-admin/plans/${p.plan_id}`, { active: activating });
      toast.success(activating ? t("plans.reactivateOk") : t("plans.retireOk"));
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("plans.error"), t));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t("plans.confirmDelete", { name: p.name }))) return;
    try {
      await api.delete(`/super-admin/plans/${p.plan_id}`);
      toast.success(t("plans.deleteOk"));
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 409 PLAN_IN_USE : `detail` est un OBJET {code, tenants, subscriptions, message}
      // (super_admin_routes.py:442), pas une chaîne — un toast direct rendrait
      // "[object Object]". Le `.message` backend est en anglais seulement ; on reforme
      // le texte nous-mêmes pour rester FR/EN/ES cohérent.
      if (e?.response?.status === 409 && detail?.code === "PLAN_IN_USE") {
        toast.error(t("plans.deleteInUse", { tenants: detail.tenants, subscriptions: detail.subscriptions }));
      } else {
        toast.error(typeof detail === "string" ? detail : t("plans.error"));
      }
    }
  };

  // ---- create dialog ----
  const openCreate = () => { setCreateForm(emptyCreateForm()); setCreateErrors(null); setCreateOpen(true); };

  const updateCreateField = (field, value) => {
    setCreateForm((f) => ({ ...f, [field]: value }));
    setCreateErrors((prev) => (prev ? { ...prev, [field === "plan_id" ? "planId" : field]: null } : prev));
  };
  const updateCreatePrice = (kind, cur, value) => {
    setCreateForm((f) => ({ ...f, [kind]: { ...f[kind], [cur]: value } }));
    setCreateErrors((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [kind]: { ...prev[kind] } };
      delete next[kind][cur];
      if (kind === "monthly") next.priceOneRequired = null;
      return next;
    });
  };

  const submitCreate = async () => {
    const err = computeCreateErrors(t, tc, createForm);
    if (err) {
      setCreateErrors(err);
      toast.error(tc("validation.fixHighlighted"));
      return;
    }
    setCreateErrors(null);
    setCreating(true);
    try {
      await api.post("/super-admin/plans", {
        plan_id: createForm.plan_id,
        name: createForm.name,
        description: createForm.description,
        tagline: createForm.tagline,
        order: Number(createForm.order),
        active: createForm.active,
        prices: toPriceGrid(createForm.monthly),
        annual_prices: toPriceGrid(createForm.annual),
      });
      toast.success(t("plans.createOk"));
      setCreateOpen(false);
      load();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(t("plans.planIdTaken"));
      } else {
        toast.error(extractApiError(e, t("plans.error"), t));
      }
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;
  if (loadError) return <PageLoadError error={loadError} onRetry={load} testId="plans-load-error" />;

  return (
    <div data-testid="sa-plans">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <h1 className="font-sol font-semibold text-4xl">{t("plans.title")}</h1>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" data-testid="plan-features-link" className="rounded-full border-slate-200">
            <Link to="/super-admin/plan-features">
              <Table2 className="w-4 h-4 mr-2" /> {t("plans.manageFeatures")}
            </Link>
          </Button>
          <Button
            onClick={openCreate}
            data-testid="plan-create"
            className="bg-sol-gold hover:bg-sol-gold/90 text-black rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" /> {t("plans.create")}
          </Button>
        </div>
      </div>
      <p className="text-slate-500 mb-12">{t("plans.subtitle")}</p>

      <div className="grid lg:grid-cols-4 gap-6">
        {plans.map((p) => {
          const d = drafts[p.plan_id];
          if (!d) return null;
          const err = errors[p.plan_id];
          return (
            <div
              key={p.plan_id}
              data-testid={`plan-card-${p.plan_id}`}
              className={`rounded-2xl border p-6 bg-white ${p.active ? "border-slate-200" : "border-slate-200 opacity-70"}`}
            >
              <div className="flex items-center justify-between mb-1 gap-2">
                <Input
                  value={d.name}
                  onChange={(ev) => updateField(p.plan_id, "name", ev.target.value)}
                  className="bg-white shadow-sm border-slate-200 text-slate-800 font-sol font-semibold text-xl"
                  data-testid={`plan-name-${p.plan_id}`}
                />
                {!p.active && (
                  <Badge variant="secondary" data-testid={`plan-retired-badge-${p.plan_id}`} className="shrink-0">
                    {t("plans.retiredBadge")}
                  </Badge>
                )}
              </div>
              <div className="text-[11px] text-slate-400 mb-3">{p.plan_id}</div>

              <Input
                value={d.tagline}
                onChange={(ev) => updateField(p.plan_id, "tagline", ev.target.value)}
                placeholder={t("plans.taglinePh")}
                className="bg-white shadow-sm border-slate-200 text-slate-700 text-sm mb-3"
                data-testid={`plan-tagline-${p.plan_id}`}
              />
              <Textarea
                value={d.description}
                onChange={(ev) => updateField(p.plan_id, "description", ev.target.value)}
                className="w-full bg-white shadow-sm border-slate-200 text-sm text-slate-700 mb-4 resize-none"
                rows={2}
                data-testid={`plan-description-${p.plan_id}`}
              />
              <div className="mb-4">
                <MediaPickerField
                  label={t("plans.image")}
                  value={d.image}
                  onChange={(picked) => updateField(p.plan_id, "image", picked)}
                  onClear={() => updateField(p.plan_id, "image", null)}
                  testId={`sa-plan-image-${p.plan_id}`}
                />
              </div>

              <div className="mb-4">
                <Label className="text-xs text-slate-400">{t("plans.monthly")}</Label>
                <p className="text-[11px] text-slate-400 mb-1">{t("plans.monthlyHelp")}</p>
                <div className="space-y-2">
                  {OPERATING_CURRENCIES.map((cur) => (
                    <div key={cur}>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-400 w-10">{currencyLabel(cur)}</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={d.monthly[cur]}
                          onChange={(ev) => updatePrice(p.plan_id, "monthly", cur, ev.target.value)}
                          data-testid={`plan-price-${p.plan_id}-${cur}`}
                          className={`bg-white shadow-sm text-slate-800 ${err?.monthly?.[cur] ? invalidInputClass : "border-slate-200"}`}
                        />
                        <span className="text-xs text-slate-400">{t("plans.perMonth")}</span>
                      </div>
                      <FieldError
                        message={err?.monthly?.[cur] || ""}
                        testId={`plan-price-${p.plan_id}-${cur}-error`}
                        className="ml-[3.25rem] mt-0.5"
                      />
                    </div>
                  ))}
                </div>
                <FieldError
                  message={err?.priceOneRequired || ""}
                  testId={`plan-price-one-required-${p.plan_id}`}
                />
                <PublicVisibility t={t} draft={d} planId={p.plan_id} />
              </div>

              <div className="mb-4">
                <Label className="text-xs text-slate-400">{t("plans.annual")}</Label>
                <p className="text-[11px] text-slate-400 mb-1">{t("plans.annualHelp")}</p>
                <div className="space-y-2">
                  {OPERATING_CURRENCIES.map((cur) => (
                    <div key={cur}>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-400 w-10">{currencyLabel(cur)}</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={d.annual[cur]}
                          onChange={(ev) => updatePrice(p.plan_id, "annual", cur, ev.target.value)}
                          data-testid={`plan-annual-${p.plan_id}-${cur}`}
                          className={`bg-white shadow-sm text-slate-800 ${err?.annual?.[cur] ? invalidInputClass : "border-slate-200"}`}
                        />
                        <span className="text-xs text-slate-400">{t("plans.perYear")}</span>
                      </div>
                      <FieldError
                        message={err?.annual?.[cur] || ""}
                        testId={`plan-annual-${p.plan_id}-${cur}-error`}
                        className="ml-[3.25rem] mt-0.5"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <Label className="text-xs text-slate-400">{t("plans.order")}</Label>
                <Input
                  type="number"
                  value={d.order}
                  onChange={(ev) => updateField(p.plan_id, "order", ev.target.value)}
                  className={`bg-white shadow-sm text-slate-800 mt-1 ${err?.order ? invalidInputClass : "border-slate-200"}`}
                  data-testid={`plan-order-${p.plan_id}`}
                />
                <FieldError message={err?.order || ""} testId={`plan-order-${p.plan_id}-error`} />
              </div>

              <Button
                onClick={() => save(p)}
                disabled={saving[p.plan_id]}
                data-testid={`save-plan-${p.plan_id}`}
                className="w-full bg-sol-gold hover:bg-sol-gold/90 text-black rounded-full mb-2"
              >
                {saving[p.plan_id] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t("plans.save")}
              </Button>
              <div className="flex gap-2">
                <Button
                  onClick={() => toggleActive(p)}
                  variant="outline"
                  data-testid={`plan-retire-${p.plan_id}`}
                  className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-full"
                >
                  {p.active ? <Ban className="w-3.5 h-3.5 mr-1.5" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                  {p.active ? t("plans.retire") : t("plans.reactivate")}
                </Button>
                <Button
                  onClick={() => remove(p)}
                  variant="outline"
                  data-testid={`plan-delete-${p.plan_id}`}
                  className="border-rose-200 text-rose-600 hover:bg-rose-50 rounded-full"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) setCreateErrors(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="plan-create-dialog">
          <DialogHeader>
            <DialogTitle>{t("plans.createTitle")}</DialogTitle>
            <DialogDescription>{t("plans.createSubtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">{t("plans.planId")}</Label>
              <Input
                value={createForm.plan_id}
                onChange={(e) => updateCreateField("plan_id", e.target.value)}
                data-testid="plan-create-id"
                className={`mt-1 ${createErrors?.planId ? "border-rose-400" : ""}`}
              />
              <p className={`text-[11px] mt-1 ${createErrors?.planId ? "text-rose-500" : "text-slate-400"}`}>
                {createErrors?.planId || t("plans.planIdHelp")}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("plans.name")}</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => updateCreateField("name", e.target.value)}
                  className={`mt-1 ${createErrors?.name ? invalidInputClass : ""}`}
                  data-testid="plan-create-name"
                />
                <FieldError message={createErrors?.name || ""} testId="plan-create-name-error" />
              </div>
              <div>
                <Label className="text-xs">{t("plans.tagline")}</Label>
                <Input value={createForm.tagline} onChange={(e) => updateCreateField("tagline", e.target.value)} placeholder={t("plans.taglinePh")} className="mt-1" data-testid="plan-create-tagline" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("plans.description")}</Label>
              <Textarea value={createForm.description} onChange={(e) => updateCreateField("description", e.target.value)} rows={2} className="mt-1" data-testid="plan-create-description" />
            </div>

            <div>
              <Label className="text-xs text-slate-400">{t("plans.monthly")}</Label>
              <p className="text-[11px] text-slate-400 mb-1">{t("plans.monthlyHelp")}</p>
              <div className="grid grid-cols-2 gap-2">
                {OPERATING_CURRENCIES.map((cur) => (
                  <div key={cur}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-10">{currencyLabel(cur)}</span>
                      <Input
                        type="number" step="0.01"
                        value={createForm.monthly[cur]}
                        onChange={(e) => updateCreatePrice("monthly", cur, e.target.value)}
                        data-testid={`plan-create-price-${cur}`}
                        className={createErrors?.monthly?.[cur] ? "border-rose-400" : ""}
                      />
                    </div>
                    <FieldError message={createErrors?.monthly?.[cur] || ""} testId={`plan-create-price-${cur}-error`} className="ml-12 mt-0.5" />
                  </div>
                ))}
              </div>
              <FieldError message={createErrors?.priceOneRequired || ""} testId="plan-create-price-one-required" />
            </div>

            <div>
              <Label className="text-xs text-slate-400">{t("plans.annual")}</Label>
              <p className="text-[11px] text-slate-400 mb-1">{t("plans.annualHelp")}</p>
              <div className="grid grid-cols-2 gap-2">
                {OPERATING_CURRENCIES.map((cur) => (
                  <div key={cur}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-10">{currencyLabel(cur)}</span>
                      <Input
                        type="number" step="0.01"
                        value={createForm.annual[cur]}
                        onChange={(e) => updateCreatePrice("annual", cur, e.target.value)}
                        data-testid={`plan-create-annual-${cur}`}
                        className={createErrors?.annual?.[cur] ? "border-rose-400" : ""}
                      />
                    </div>
                    <FieldError message={createErrors?.annual?.[cur] || ""} testId={`plan-create-annual-${cur}-error`} className="ml-12 mt-0.5" />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs">{t("plans.order")}</Label>
                <Input
                  type="number"
                  value={createForm.order}
                  onChange={(e) => updateCreateField("order", e.target.value)}
                  className={`mt-1 ${createErrors?.order ? "border-rose-400" : ""}`}
                  data-testid="plan-create-order"
                />
                <FieldError message={createErrors?.order || ""} testId="plan-create-order-error" />
              </div>
              <div className="flex items-center gap-2 pb-1">
                <Switch checked={createForm.active} onCheckedChange={(v) => updateCreateField("active", v)} data-testid="plan-create-active" />
                <Label className="text-xs">{t("plans.active")}</Label>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => { setCreateOpen(false); setCreateErrors(null); }} className="flex-1 rounded-full">
                {t("plans.cancel")}
              </Button>
              <Button
                onClick={submitCreate}
                disabled={creating}
                data-testid="plan-create-submit"
                className="flex-1 bg-sol-gold hover:bg-sol-gold/90 text-black rounded-full"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                {t("plans.createSubmit")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
